import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { Checkbox, Title } from '@xsolla/ui-core';
import { AlertDangerColorful } from '@xsolla/ui-icons';
import { Modal } from '@xsolla/ui-modal';

import { removeOAuthAuth } from '../../../api';
import { OauthSettings } from '../../../api/configurations/oauth2/types';
import { truncateString } from '../../../utils';
import { FormFooter } from '../../ui';
import { DeleteClientSubtitle, SuccessModalWrapper } from './style';

type Props = {
  id: number;
  name: string;
  setOauthSettings: React.Dispatch<React.SetStateAction<OauthSettings[]>>;
  onClose: () => void;
};

export const RemoveOauthModal = ({ setOauthSettings, onClose, id, name }: Props): JSX.Element => {
  const intl = useIntl();
  const [agreedWarning, setAgreedWarning] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const removeHandle = async () => {
    try {
      setFetching(true);
      setError(false);
      await removeOAuthAuth(id);
      await setOauthSettings((prev) => {
        return prev.filter((item) => item?.id !== id);
      });
      onClose();
    } catch (err) {
      setError(true);
    } finally {
      setFetching(false);
    }
  };

  return (
    <Modal>
      <SuccessModalWrapper>
        <AlertDangerColorful size={40} />
        <Title center indent="sm">
          {intl.formatMessage(
            { id: 'authorization.settings.oauth2.modal.remove.title' },
            { name: truncateString(name) }
          )}
        </Title>
        <DeleteClientSubtitle>
          {intl.messages['authorization.settings.oauth2.modal.remove.subtitle']}
        </DeleteClientSubtitle>
        <Checkbox
          data-test-id="oauth2-delete-checkbox"
          onChange={(e) => setAgreedWarning(e.target.checked)}>
          <b>{intl.messages['authorization.settings.oauth2.modal.remove.checkbox']}</b>
        </Checkbox>
      </SuccessModalWrapper>

      <FormFooter
        isModal
        onSubmit={removeHandle}
        onCancel={onClose}
        fetching={fetching}
        error={error}
        disabled={!agreedWarning}
        submitText={intl.formatMessage({ id: 'authorization.settings.oauth2.modal.remove.submit' })}
        submitButtonPalette="danger"
      />
    </Modal>
  );
};
